/**
 * useInquiryContacts
 *
 * 基于 inquiry-contact/list 接口拉取询盘联系人列表 + 分页 + 筛选 + 展开回复。
 *
 * 改造要点：
 *  - 数据源从 inquiryDetailsCompanyListGet（IApiCompanyDto）切换为
 *    listInquiryContactsGet（IApiInquiryContactResp）
 *  - 保持向上 companies 兼容字段（映射 IApiCompanyDto），后续可废弃
 *  - 'log' tab 不发 contact list 请求
 *  - filter 切换重置 page=1
 *  - 翻页时清空展开
 *  - 展开时若回复为空则自动收起
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { listInquiryContactsGet } from '@/services/swagger/easourcing/TaskReplyService';
import { taskReplyListGet } from '@/services/swagger/easourcing/InquiryService';
import {
  AGENT_PROGRESS_PAGE_SIZE,
  type AgentContactFilterType,
  type IApiCompanyDto,
  type IApiInquiryContactResp,
  type IApiTaskReplyDto,
  type UseInquiryContactsResult,
} from '../types';

export interface UseInquiryContactsOptions {
  inquiryId: string;
  pageSize?: number;
}

/**
 * 将 IApiInquiryContactResp 映射为兼容的 IApiCompanyDto（过渡用）。
 * 仅填充 view-model 实际使用的字段，其余置为安全默认值。
 */
function toCompanyDto(c: IApiInquiryContactResp): IApiCompanyDto {
  return {
    companyId: c.contactId ?? '',
    companyName: c.companyName ?? '',
    companyTaskId: c.inquiryContactId ?? '',
    country: '',
    index: 0,
    location: '',
    reachStatus: c.inquiryContactStatus === 'COMPLETED' ? 0 : c.inquiryContactStatus === 'AWAITING_RESPONSE' ? 1 : 2,
    replyCount: 0,
    sentCount: 0,
    status: c.inquiryContactStatus === 'COMPLETED' ? 3 : 1,
    totalCount: 0,
    unreadCount: 0,
  };
}

export function useInquiryContacts(options: UseInquiryContactsOptions): UseInquiryContactsResult {
  const { inquiryId, pageSize = AGENT_PROGRESS_PAGE_SIZE } = options;

  const [contacts, setContacts] = useState<IApiInquiryContactResp[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPageState] = useState(1);
  const [filter, setFilterState] = useState<AgentContactFilterType>('all');
  const [loading, setLoading] = useState(false);
  const [repliesMap, setRepliesMap] = useState<Record<string, IApiTaskReplyDto[]>>({});
  const [loadingReplies, setLoadingReplies] = useState<Set<string>>(new Set());
  const [expandedCompanyTaskId, setExpandedCompanyTaskId] = useState<string | null>(null);

  /** useMemoizedFn 保证引用稳定，同时使用最新闭包 */
  const fetchPage = useMemoizedFn(async (targetPage: number) => {
    setLoading(true);
    try {
      const res = await listInquiryContactsGet({
        inquiryTaskId: inquiryId,
        pageNum: targetPage,
        pageSize,
      });
      if (res?.data) {
        setContacts(res.data ?? []);
        setTotal(res.total ?? 0);
      }
    } catch (error) {
      console.error('Failed to fetch inquiry contacts:', error);
    } finally {
      setLoading(false);
    }
  });

  /** 副作用：page / filter 变化（且非 log tab）→ 拉取 */
  useEffect(() => {
    if (filter === 'log') return;
    void fetchPage(page);
  }, [page, filter, fetchPage]);

  /** 兼容旧 companies 引用：将 IApiInquiryContactResp[] 映射为 IApiCompanyDto[] */
  const companies = useMemo(() => contacts.map(toCompanyDto), [contacts]);

  const setPage = useCallback((targetPage: number) => {
    setPageState(targetPage);
    setExpandedCompanyTaskId(null);
  }, []);

  const setFilter = useCallback((targetFilter: AgentContactFilterType) => {
    setFilterState(targetFilter);
    setPageState(1);
  }, []);

  const fetchReplies = useCallback(async (companyTaskId: string, onFetched?: (replies: IApiTaskReplyDto[]) => void) => {
    setLoadingReplies(prev => new Set(prev).add(companyTaskId));
    try {
      const res = await taskReplyListGet({ taskId: companyTaskId });
      const replies = res?.data?.replies ?? [];
      setRepliesMap(prev => ({ ...prev, [companyTaskId]: replies }));
      onFetched?.(replies);
    } catch (error) {
      console.error('Failed to fetch replies:', error);
      onFetched?.([]);
    } finally {
      setLoadingReplies(prev => {
        const next = new Set(prev);
        next.delete(companyTaskId);
        return next;
      });
    }
  }, []);

  const toggleExpand = useCallback(
    (companyTaskId: string) => {
      if (expandedCompanyTaskId === companyTaskId) {
        setExpandedCompanyTaskId(null);
        return;
      }
      setExpandedCompanyTaskId(companyTaskId);
      void fetchReplies(companyTaskId, replies => {
        if (replies.length === 0) {
          setExpandedCompanyTaskId(prev => (prev === companyTaskId ? null : prev));
        }
      });
    },
    [expandedCompanyTaskId, fetchReplies]
  );

  const refresh = useCallback(async () => {
    if (filter === 'log') return;
    await fetchPage(page);
    if (expandedCompanyTaskId) await fetchReplies(expandedCompanyTaskId);
  }, [filter, fetchPage, page, expandedCompanyTaskId, fetchReplies]);

  return useMemo(
    () => ({
      contacts,
      companies,
      total,
      page,
      pageSize,
      filter,
      loading,
      repliesMap,
      loadingReplies,
      expandedCompanyTaskId,
      setPage,
      setFilter,
      refresh,
      fetchReplies,
      toggleExpand,
      setExpandedCompanyTaskId,
    }),
    [
      contacts,
      companies,
      total,
      page,
      pageSize,
      filter,
      loading,
      repliesMap,
      loadingReplies,
      expandedCompanyTaskId,
      setPage,
      setFilter,
      refresh,
      fetchReplies,
      toggleExpand,
    ]
  );
}

export default useInquiryContacts;
